import type { TokenIndicativeCost } from '@ace-tracker/shared';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip } from './InfoTooltip';

/**
 * Indicative cost per model for a token-priced tool (Claude Code, Codex),
 * showing which Settings pricing row each model matched. Models on the
 * fallback rate are labelled in text, not just color.
 */
export function ModelCostTable({
  cost,
  formatCurrency,
  inputLabel,
  cacheReadLabel,
  showCacheWrite,
  info,
}: {
  cost: TokenIndicativeCost;
  formatCurrency: (value: number, currency: string) => string;
  /** e.g. "Billable input" for Codex, where cached input is priced separately. */
  inputLabel: string;
  cacheReadLabel: string;
  showCacheWrite: boolean;
  info?: string;
}) {
  return (
    <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
      <ExportMenu title="Indicative cost by model" className="float-right -mr-2 -mt-2 ml-2" />
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
        Indicative cost by model
        {info && <InfoTooltip text={info} />}
      </h3>
      <p className="mt-1 text-xs text-text-muted">
        Each model priced at its row in Settings → Model pricing.
        {cost.unpricedModels.length > 0 &&
          ` ${cost.unpricedModels.length} model${cost.unpricedModels.length === 1 ? '' : 's'} used the fallback rate — add pricing in Settings for an accurate figure.`}
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Model</th>
              <th className="pb-2.5 pr-3">Priced as</th>
              <th className="pb-2.5 pr-3 text-right">{inputLabel}</th>
              <th className="pb-2.5 pr-3 text-right">Output</th>
              <th className="pb-2.5 pr-3 text-right">{cacheReadLabel}</th>
              {showCacheWrite && <th className="pb-2.5 pr-3 text-right">Cache write</th>}
              <th className="pb-2.5 text-right">Cost</th>
            </tr>
          </thead>
          <tbody>
            {cost.byModel.map((m) => (
              <tr key={m.model} className="border-b border-border last:border-0">
                <td className="py-2.5 pr-3 text-text-default">{m.model}</td>
                <td className="py-2.5 pr-3">
                  {m.pricedAs ? (
                    <span className="font-mono text-xs text-text-muted">{m.pricedAs}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-input bg-warn-bg px-1.5 py-0.5 text-xs text-text-default">
                      <i aria-hidden="true" className="fa-solid fa-triangle-exclamation text-warn-ink" />
                      Fallback rate
                    </span>
                  )}
                </td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{m.inputTokens.toLocaleString()}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{m.outputTokens.toLocaleString()}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{m.cacheReadTokens.toLocaleString()}</td>
                {showCacheWrite && <td className="py-2.5 pr-3 text-right tabular-nums">{m.cacheWriteTokens.toLocaleString()}</td>}
                <td className="py-2.5 text-right font-medium tabular-nums text-text-default">{formatCurrency(m.cost, cost.currency)}</td>
              </tr>
            ))}
            {cost.byModel.length === 0 && (
              <tr>
                <td className="py-3 text-text-muted" colSpan={showCacheWrite ? 7 : 6}>
                  No token usage in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
